import { ArrowLeft } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { PhotoMatchingPanel } from '@/components/faces/PhotoMatchingPanel'
import {
  type IdentityTab,
  IdentityWorkspace,
  type IdentityWorkspaceContent,
} from '@/components/identity/IdentityWorkspace'
import { Button } from '@/components/ui/button'
import { UnassignedFacesTab } from '@/features/identities/UnassignedFacesTab'
import { UnassignedVoicesTab } from '@/features/identities/voices/UnassignedVoicesTab'
import { IdentityManager } from '@/features/identity/IdentityManager'
import CaseIdentityDetailPage from '@/pages/cases/identity-detail'

interface IdentityPanelProps {
  caseId: string
  /** Advanced Face Detection is on (ADR-0051): the face tabs are offered. */
  faceDetection: boolean
  /** HUMINT is on (ADR-0077): the Unassigned voices tab is offered. */
  humint?: boolean
}

export function IdentityPanel({ caseId, faceDetection, humint = false }: IdentityPanelProps) {
  const { t } = useTranslation()
  const [selectedIdentityId, setSelectedIdentityId] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<IdentityTab>('identities')

  const identities = <IdentityManager caseId={caseId} onViewIdentity={setSelectedIdentityId} />
  const content: IdentityWorkspaceContent = {
    identities,
    ...(faceDetection
      ? {
          unassigned: <UnassignedFacesTab caseId={caseId} />,
          matches: <PhotoMatchingPanel caseId={caseId} onOpenIdentity={setSelectedIdentityId} />,
        }
      : {}),
    ...(humint ? { 'unassigned-voices': <UnassignedVoicesTab caseId={caseId} /> } : {}),
  }

  if (selectedIdentityId) {
    return (
      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
        <div className="border-b border-border px-2 py-2 md:px-6 md:py-3">
          <Button variant="ghost" size="sm" onClick={() => setSelectedIdentityId(null)}>
            <ArrowLeft className="size-4" />
            {t('identity.backToList')}
          </Button>
        </div>
        {/* The identity on show was merged into another one (ADR-0052, ADR-0053):
            the page says so, and the selection moves to the survivor. */}
        <CaseIdentityDetailPage
          caseId={caseId}
          identityId={selectedIdentityId}
          initialTab={activeTab === 'matches' ? 'possible-matches' : 'overview'}
          onMerged={setSelectedIdentityId}
        />
      </div>
    )
  }

  return <IdentityWorkspace activeTab={activeTab} onTabChange={setActiveTab} content={content} />
}
